import * as React from "react";
import { Controller, FieldValues, UseControllerProps } from "react-hook-form";
import { InputError } from "@monorepo/components";
import theme from "@monorepo/styles/theme";
import { getLocale } from "@monorepo/translations/utils";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import DatePicker from "@mui/lab/DatePicker";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import { InputAdornment, InputBase, InputLabel, styled } from "@mui/material";
import Box from "@mui/system/Box";
import { format } from "date-fns";

type TDatepickerProps<TFieldValues extends FieldValues> = UseControllerProps<TFieldValues> & {
  id: string;
  error?: string;
  label: string;
  adornment: React.ReactNode;
  disabled?: boolean;
  disabledDirectInput?: boolean;
  disableFuture?: boolean;
};

const StyledInput = styled(InputBase)((disabled) => ({
  width: "100%",
  "& .MuiInputBase-input": {
    width: "100%",
    borderBottom: "transparent",
    fontSize: theme.typography.body2.fontSize,
    height: "unset",
    padding: "6px 6px 3px 3px",
    fontWeight: "bold",
    "&:focus": {
      borderColor: theme.palette.primary.main,
    },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    textFillColor: disabled ? `${theme.palette.text.disabled}` : "inherit",
    fontWeight: disabled ? "inherit" : "bold",
    pointerEvents: "none",
    borderBottom: 0,
  },
}));

export function Datepicker<TFieldValues extends FieldValues>({
  id,
  name,
  label,
  control,
  defaultValue,
  error,
  adornment,
  disabled = false,
  disabledDirectInput = false,
  disableFuture = false,
}: TDatepickerProps<TFieldValues>) {
  const [open, setOpen] = React.useState(false);
  const input = React.useRef(null);

  const handleAdornmentClick = () => {
    setOpen(true);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={getLocale()}>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => {
          field.ref(input.current);
          return (
            <DatePicker
              disableFuture={disableFuture}
              inputRef={input}
              mask={"____-__-__"}
              inputFormat={"yyyy-MM-dd"}
              value={field.value}
              open={open}
              disabled={disabled}
              onClose={() => {
                setOpen(false);
              }}
              onAccept={() => {
                setOpen(false);
              }}
              onChange={(value: string) => field.onChange(value ? format(new Date(value), "yyyy-MM-dd") : null)}
              renderInput={(params: any) => (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    "& > label": {
                      color: theme.palette.text.secondary,
                    },
                  }}
                >
                  <InputLabel sx={{ fontSize: "14px" }} htmlFor={id}>
                    {label}
                  </InputLabel>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      background: "transparent",
                      "& > input": {
                        background: "transparent",
                      },
                    }}
                  >
                    <StyledInput
                      type="text"
                      inputRef={params.inputRef}
                      inputProps={{
                        ...params.inputProps,
                        disabled: disabledDirectInput,
                      }}
                      value={params.value}
                      disabled={disabled ? true : params.disabled}
                      onClick={params.onClick}
                      onChange={params.onChange}
                      sx={{ paddingRight: "6px" }}
                      endAdornment={
                        disabled ? null : (
                          <InputAdornment onClick={handleAdornmentClick} position="end">
                            {adornment}
                          </InputAdornment>
                        )
                      }
                    />
                  </Box>
                  {error && <InputError>{error}</InputError>}
                </Box>
              )}
            />
          );
        }}
      />
    </LocalizationProvider>
  );
}
